import React from 'react';

/* ------------------------------------------------------------------ */
/*  Consent Mode v2 + GA4 Modelleme — mindmap'li kısa anlatım          */
/*  Statik eğitim sayfası. /egitim/consent-mode-v2                     */
/* ------------------------------------------------------------------ */

/* --------------------------- mindmap motoru --------------------------- */
// Kaba satır kırma: SVG'de otomatik wrap yok, elle yapıyoruz.
function wrap(text, max = 30) {
  const out = [];
  let line = '';
  for (const w of text.split(' ')) {
    if (!line) line = w;
    else if ((line + ' ' + w).length <= max) line += ' ' + w;
    else { out.push(line); line = w; }
  }
  if (line) out.push(line);
  return out;
}

const TONES = {
  indigo: { fill: '#eef2ff', stroke: '#c7d2fe', text: '#3730a3' },
  emerald: { fill: '#ecfdf5', stroke: '#a7f3d0', text: '#065f46' },
  amber: { fill: '#fffbeb', stroke: '#fde68a', text: '#92400e' },
  sky: { fill: '#f0f9ff', stroke: '#bae6fd', text: '#075985' },
  rose: { fill: '#fff1f2', stroke: '#fecdd3', text: '#9f1239' },
  slate: { fill: '#f8fafc', stroke: '#e2e8f0', text: '#334155' },
};

const LH = 13;      // satır yüksekliği
const GAP = 26;     // dallar arası boşluk
const KID_GAP = 8;  // yavru kutular arası boşluk

function Mindmap({ center, left, right }) {
  const build = (branches) =>
    branches.map((b) => {
      const kids = b.items.map((t) => {
        const lines = wrap(t, 30);
        return { lines, h: Math.max(30, lines.length * LH + 15) };
      });
      const kidsH = kids.reduce((s, k) => s + k.h + KID_GAP, -KID_GAP);
      return { ...b, kids, h: Math.max(48, kidsH) };
    });

  const L = build(left);
  const R = build(right);
  const totalH = (arr) => arr.reduce((s, b) => s + b.h + GAP, -GAP);
  const H = Math.max(totalH(L), totalH(R)) + 56;
  const cy = H / 2;

  // her dalın y konumunu hesapla
  const place = (arr) => {
    let y = (H - totalH(arr)) / 2;
    return arr.map((b) => {
      const top = y;
      y += b.h + GAP;
      let ky = top + (b.h - b.kids.reduce((s, k) => s + k.h + KID_GAP, -KID_GAP)) / 2;
      const kids = b.kids.map((k) => {
        const kTop = ky;
        ky += k.h + KID_GAP;
        return { ...k, top: kTop, cy: kTop + k.h / 2 };
      });
      return { ...b, top, cy: top + b.h / 2, kids };
    });
  };

  const LP = place(L);
  const RP = place(R);

  const Node = ({ x, y, w, h, tone, label, bold, size }) => {
    const t = TONES[tone] || TONES.slate;
    const lines = Array.isArray(label) ? label : wrap(label, 22);
    const startY = y + h / 2 - ((lines.length - 1) * LH) / 2 + 1;
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx={10} fill={t.fill} stroke={t.stroke} strokeWidth="1.5" />
        {lines.map((ln, i) => (
          <text
            key={i}
            x={x + w / 2}
            y={startY + i * LH}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={size || 12}
            fontWeight={bold ? 700 : 600}
            fill={t.text}
          >
            {ln}
          </text>
        ))}
      </g>
    );
  };

  const Kid = ({ x, k, tone }) => {
    const t = TONES[tone] || TONES.slate;
    const startY = k.cy - ((k.lines.length - 1) * LH) / 2;
    return (
      <g>
        <rect x={x} y={k.top} width={192} height={k.h} rx={8} fill="#ffffff" stroke={t.stroke} strokeWidth="1.25" />
        {k.lines.map((ln, i) => (
          <text key={i} x={x + 11} y={startY + i * LH} dominantBaseline="middle" fontSize={11} fill="#475569">
            {ln}
          </text>
        ))}
      </g>
    );
  };

  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <svg viewBox={`0 0 1040 ${H}`} className="w-full min-w-[880px]" role="img">
        {/* bağlantılar: merkez → dal */}
        {LP.map((b, i) => (
          <path key={`lc${i}`} d={`M 430 ${cy} C 412 ${cy}, 410 ${b.cy}, 392 ${b.cy}`} fill="none" stroke={TONES[b.tone].stroke} strokeWidth="2" />
        ))}
        {RP.map((b, i) => (
          <path key={`rc${i}`} d={`M 610 ${cy} C 628 ${cy}, 630 ${b.cy}, 648 ${b.cy}`} fill="none" stroke={TONES[b.tone].stroke} strokeWidth="2" />
        ))}
        {/* bağlantılar: dal → yavru */}
        {LP.map((b, i) =>
          b.kids.map((k, j) => (
            <path key={`lk${i}-${j}`} d={`M 232 ${b.cy} C 222 ${b.cy}, 222 ${k.cy}, 212 ${k.cy}`} fill="none" stroke={TONES[b.tone].stroke} strokeWidth="1.25" />
          ))
        )}
        {RP.map((b, i) =>
          b.kids.map((k, j) => (
            <path key={`rk${i}-${j}`} d={`M 808 ${b.cy} C 818 ${b.cy}, 818 ${k.cy}, 828 ${k.cy}`} fill="none" stroke={TONES[b.tone].stroke} strokeWidth="1.25" />
          ))
        )}

        {/* merkez */}
        <Node x={430} y={cy - 29} w={180} h={58} tone="slate" label={center} bold size={14} />

        {/* dallar + yavrular */}
        {LP.map((b, i) => (
          <g key={`l${i}`}>
            <Node x={232} y={b.cy - 24} w={160} h={48} tone={b.tone} label={b.label} bold size={12.5} />
            {b.kids.map((k, j) => <Kid key={j} x={20} k={k} tone={b.tone} />)}
          </g>
        ))}
        {RP.map((b, i) => (
          <g key={`r${i}`}>
            <Node x={648} y={b.cy - 24} w={160} h={48} tone={b.tone} label={b.label} bold size={12.5} />
            {b.kids.map((k, j) => <Kid key={j} x={828} k={k} tone={b.tone} />)}
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ------------------------------ UI atoms ------------------------------ */
function Card({ title, sub, children }) {
  return (
    <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 sm:p-7 mb-6">
      <div className="mb-5">
        <h2 className="text-[18px] font-bold text-slate-900 tracking-tight">{title}</h2>
        {sub && <p className="text-[13px] text-slate-500 mt-1 leading-6">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

const NOTE_TONES = {
  amber: ['bg-amber-50', 'border-amber-400', 'text-amber-900', '💡'],
  blue: ['bg-sky-50', 'border-sky-400', 'text-sky-900', 'ℹ️'],
  rose: ['bg-rose-50', 'border-rose-400', 'text-rose-900', '⚠️'],
  emerald: ['bg-emerald-50', 'border-emerald-400', 'text-emerald-900', '✅'],
};
function Note({ children, tone = 'amber' }) {
  const [bg, bd, tx, icon] = NOTE_TONES[tone];
  return (
    <div className={`flex gap-3 rounded-xl border-l-4 ${bd} ${bg} ${tx} px-4 py-3 text-[13px] leading-relaxed my-4`}>
      <span className="text-base leading-none mt-0.5 select-none">{icon}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/* akış şeridi: ziyaretçiden rapora kadar */
const FLOW = [
  { t: 'Ziyaretçi siteye girer', s: 'Henüz seçim yok → varsayılan: denied', tone: 'slate' },
  { t: 'Çerez banner’ı', s: 'Kabul mü, ret mi?', tone: 'indigo' },
  { t: 'Kabul → tam veri', s: 'Çerez yazılır, kullanıcı tanınır', tone: 'emerald' },
  { t: 'Ret → çerezsiz ping', s: 'Kimliksiz sinyal: sayfa, zaman, referrer', tone: 'amber' },
  { t: 'GA4 modelleme', s: 'Eksik parçayı tahmin eder', tone: 'sky' },
];
const FLOW_CLS = {
  slate: 'bg-slate-50 ring-slate-200 text-slate-700',
  indigo: 'bg-indigo-50 ring-indigo-200 text-indigo-800',
  emerald: 'bg-emerald-50 ring-emerald-200 text-emerald-800',
  amber: 'bg-amber-50 ring-amber-200 text-amber-800',
  sky: 'bg-sky-50 ring-sky-200 text-sky-800',
};

export default function ConsentModeV2() {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        {/* başlık */}
        <header className="mb-7">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 ring-1 ring-indigo-100 rounded-full px-3 py-1">
            Eğitim · 10 dakika
          </span>
          <h1 className="text-[26px] sm:text-[32px] font-extrabold text-slate-900 tracking-tight mt-3 leading-tight">
            Consent Mode v2 &amp; GA4 Modelleme
          </h1>
          <p className="text-[14px] text-slate-600 leading-7 mt-2 max-w-3xl">
            Tek cümlede: <b>Consent Mode</b>, kullanıcının çerez banner’ında verdiği cevabı Google etiketlerine
            fısıldayan sistemdir. <b>Modelleme</b> ise “hayır” diyenler yüzünden eksilen veriyi, “evet” diyenlerden
            öğrenerek tahmin etmektir.
          </p>
        </header>

        {/* 30 saniyelik özet */}
        <Card title="30 saniyede özet">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-indigo-50 ring-1 ring-indigo-100 p-4">
              <div className="text-[12px] font-bold uppercase tracking-wide text-indigo-600 mb-1">Consent Mode v2</div>
              <p className="text-[13.5px] text-slate-700 leading-6">
                Sitendeki çerez banner’ı, Google etiketlerine <b>4 ayrı sinyal</b> gönderir: reklam çerezi, reklam
                verisi, kişiselleştirme, analitik. Kullanıcı onay vermediyse etiket veriyi <b>çerez yazmadan</b>,
                kimliksiz olarak gönderir.
              </p>
            </div>
            <div className="rounded-xl bg-sky-50 ring-1 ring-sky-100 p-4">
              <div className="text-[12px] font-bold uppercase tracking-wide text-sky-600 mb-1">GA4 Modelleme</div>
              <p className="text-[13.5px] text-slate-700 leading-6">
                Google, onay veren kullanıcıların gerçek davranışını öğrenir; onay vermeyen trafikten gelen kimliksiz
                sinyallere bakarak <b>“burada muhtemelen şu kadar kullanıcı ve dönüşüm vardı”</b> der ve raporu
                tamamlar.
              </p>
            </div>
          </div>
          <Note tone="blue">
            Benzetme: sinema girişinde bilet sayan bir görevli var. Bazı seyirciler “beni sayma” diyor. Görevli onları
            tek tek saymıyor ama kapıdan kaç kişi geçtiğini görüyor. Sonra “sayılmayı kabul edenlerin” davranışına
            bakarak salondaki toplam sayıyı <b>tahmin</b> ediyor. İşte modelleme bu.
          </Note>
        </Card>

        {/* mindmap 1 */}
        <Card
          title="Mindmap 1 · Consent Mode v2"
          sub="Ortadan dışa doğru oku: neden var, nasıl çalışır, hangi sinyaller, hangi kurulum."
        >
          <Mindmap
            center={['Consent', 'Mode v2']}
            left={[
              {
                label: 'Neden var?',
                tone: 'rose',
                items: [
                  'AB/UK trafiğinde zorunlu (Mart 2024’ten beri)',
                  'Uygulanmazsa remarketing kitleleri ve dönüşüm ölçümü zayıflar',
                  'Yasal onay ile ölçüm arasında köprü kurar',
                ],
              },
              {
                label: 'Nasıl çalışır?',
                tone: 'indigo',
                items: [
                  '1) Sayfa açılır → varsayılan “denied” gönderilir',
                  '2) Kullanıcı banner’da seçimini yapar',
                  '3) Etikete “update” ile granted/denied bildirilir',
                ],
              },
            ]}
            right={[
              {
                label: '4 sinyal',
                tone: 'emerald',
                items: [
                  'analytics_storage → GA4 ölçümü',
                  'ad_storage → reklam çerezi',
                  'ad_user_data → veriyi Google’a gönderme (v2)',
                  'ad_personalization → kişiselleştirme (v2)',
                ],
              },
              {
                label: '2 kurulum modu',
                tone: 'amber',
                items: [
                  'Temel: onay yoksa etiket hiç çalışmaz → veri sıfır',
                  'Gelişmiş: etiket çalışır, çerezsiz ping gider → modelleme mümkün',
                ],
              },
            ]}
          />
          <Note tone="rose">
            En kritik ayrım burada: <b>Temel</b> modda onay vermeyen kullanıcı hiç görünmez, modelleme için besleyecek
            veri de olmaz. <b>Gelişmiş</b> modda o kullanıcı kimliksiz bir “ping” bırakır ve modelleme çalışabilir.
          </Note>
        </Card>

        {/* akış */}
        <Card title="Akış · Ziyaretçiden rapora" sub="Soldan sağa tek bir kullanıcının yolculuğu.">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch">
            {FLOW.map((f, i) => (
              <React.Fragment key={i}>
                <div className={`flex-1 rounded-xl ring-1 p-3 ${FLOW_CLS[f.tone]}`}>
                  <div className="text-[13px] font-bold leading-5">{f.t}</div>
                  <div className="text-[11.5px] opacity-80 leading-5 mt-1">{f.s}</div>
                </div>
                {i < FLOW.length - 1 && (
                  <div className="hidden sm:flex items-center text-slate-300 font-bold">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Card>

        {/* mindmap 2 */}
        <Card
          title="Mindmap 2 · GA4 Modelleme"
          sub="Neyle beslenir, ne yapar, hangi şartlar gerekir, nerede işe yaramaz."
        >
          <Mindmap
            center={['GA4', 'Modelleme']}
            left={[
              {
                label: 'Girdi (neyle beslenir?)',
                tone: 'emerald',
                items: [
                  'Onay verenler: gerçek, gözlemlenen veri',
                  'Onay vermeyenler: çerezsiz, kimliksiz ping’ler',
                ],
              },
              {
                label: 'Şartlar',
                tone: 'amber',
                items: [
                  'Gelişmiş consent mode kurulu olmalı',
                  'Yeterli hacim: onaysız trafikte günlük binlerce olay, birkaç haftalık geçmiş',
                  'Raporlama kimliği “Karma (Blended)” seçili olmalı',
                ],
              },
            ]}
            right={[
              {
                label: 'Ne yapar?',
                tone: 'sky',
                items: [
                  'Onay verenlerin davranışını öğrenir',
                  'Aynı örüntüyü onaysız trafiğe uyarlar',
                  'Eksik kullanıcı / oturum / dönüşümü tahmin eder',
                ],
              },
              {
                label: 'Sınırları',
                tone: 'rose',
                items: [
                  'BigQuery export’ta YOK — orada sadece ham veri var',
                  'Gerçek zamanlı raporda ve kullanıcı düzeyinde yok',
                  'Tahmindir; kesin gerçek değildir',
                ],
              },
            ]}
          />
          <Note tone="amber">
            Pratikte en çok kafa karıştıran nokta: <b>GA4 arayüzündeki dönüşüm sayısı BigQuery’dekinden yüksek
            çıkabilir.</b> Sebep hata değil, arayüzdeki modellenmiş veridir. İki kaynağı karşılaştırırken bunu hesaba
            kat.
          </Note>
        </Card>

        {/* kontrol listesi */}
        <Card title="Hızlı kontrol listesi" sub="Bir markada işler yolunda mı, 5 soruda anla.">
          <ol className="text-[13.5px] text-slate-700 leading-7 space-y-2 list-decimal pl-5">
            <li>Çerez banner’ı Google etiketlerine sinyal gönderiyor mu, yoksa sadece görsel mi?</li>
            <li>Kurulum <b>Gelişmiş</b> mi, <b>Temel</b> mi? (Temelse modelleme hiç çalışmaz.)</li>
            <li>Varsayılan durum sayfa açılışında <b>denied</b> mı, sonra <b>update</b> geliyor mu?</li>
            <li>v2’nin eklediği <code className="px-1 py-0.5 rounded bg-slate-100 text-[12px]">ad_user_data</code> ve <code className="px-1 py-0.5 rounded bg-slate-100 text-[12px]">ad_personalization</code> gönderiliyor mu?</li>
            <li>GA4 raporlama kimliği <b>Karma</b> mı? Değilse modellenmiş veriyi hiç görmezsin.</li>
          </ol>
          <Note tone="emerald">
            Trafik veya dönüşüm bir anda düştüğünde ilk soru “performans ne oldu?” değil, <b>“ölçüm mü bozuldu?”</b>
            olmalı. Banner değişikliği veya yanlış consent kurulumu, bütün bir markanın verisini bir günde yarıya
            indirebilir.
          </Note>
        </Card>

        <p className="text-[12px] text-slate-400 text-center pb-8">
          Glowytics · Eğitim · /egitim/consent-mode-v2
        </p>
      </div>
    </div>
  );
}
